function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
// ----------------------------------------------------------------------------
// Snippet: Modal
// ----------------------------------------------------------------------------
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import translate from 'scripts/helpers/iamota-translate';

// Styles
import './_modal.scss';

// -------------------------------------
// Web Component
// -------------------------------------
export default class iamModal extends HTMLElement {
  connectedCallback() {
    // Initialize internal elements with aligned spacing
    this.modalContent = this.querySelector('.modal__content');
    this.modalOverlay = this.querySelector('.modal__overlay');
    this.closeButton = this.querySelector('.modal__close-button');

    // Validation
    if (!this.modalContent || !this.modalOverlay || !this.closeButton) {
      console.error('iamModal is missing required elements. Initialization aborted.', this);
      return;
    }

    // Add internal event listeners for close button, overlay, and keyboard events
    // Bind event listeners only once
    this.closeModalRef = this.closeModal.bind(this);
    this.handleKeydownRef = this.handleKeydown.bind(this);
    this.closeButton.addEventListener('click', this.closeModalRef);
    this.modalOverlay.addEventListener('click', this.closeModalRef);

    // Set up event listeners for external triggers when the element is connected
    this.triggers = document.querySelectorAll("[data-modal-target=\"".concat(this.id, "\"]"));
    this.openModalRefs = new Map();
    this.triggers.forEach(trigger => {
      var openModalRef = this.openModal.bind(this, trigger);
      this.openModalRefs.set(trigger, openModalRef);
      trigger.addEventListener('click', openModalRef);
    });
  }
  disconnectedCallback() {
    // Remove event listeners from external
    // i.e. if you delete the modal itself, the triggers should not be bound to it anymore
    this.openModalRefs.forEach((listener, trigger) => {
      trigger.removeEventListener('click', listener);
    });
    this.openModalRefs.clear();

    // Remove globally bound event listeners
    document.removeEventListener('keydown', this.handleKeydownRef);
  }
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeModal();
    } else if (event.key === 'Tab') {
      this.handleTabKey(event);
    }
  }
  handleTabKey(event) {
    if (event.shiftKey) {
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }
  openModal(trigger) {
    var _this = this;
    return _asyncToGenerator(function* () {
      // Open the modal
      _this.classList.add('modal--open');
      document.body.classList.add('noscroll');
      document.addEventListener('keydown', _this.handleKeydownRef);

      // Accessibility: set aria-hidden to false when opening the modal
      _this.setAttribute('aria-hidden', 'false');

      // Accessibility: Remember the element that triggered the modal (so we can return focus to it later)
      _this.triggerElement = trigger;

      // Enable tabindex for focusable elements when opening modal
      _this.enableFocusWithinModal();

      // Trigger custom events for modal open
      iamotaEventHandler.trigger(['modal:opened', "modal:opened:".concat(_this.id)], {
        modal: _this,
        triggerElement: trigger
      }, {
        context: _this
      });
    })();
  }
  closeModal() {
    // Close the modal
    this.classList.remove('modal--open');
    document.body.classList.remove('noscroll');
    document.removeEventListener('keydown', this.handleKeydownRef);

    // Disable tabindex for focusable elements when closing modal
    this.disableFocusWithinModal();

    // Accessibility: set aria-hidden to true when closing the modal
    this.setAttribute('aria-hidden', 'true');

    // Trigger custom events for modal close
    iamotaEventHandler.trigger(['modal:closed', "modal:closed:".concat(this.id)], {
      modal: this,
      triggerElement: this.triggerElement
    }, {
      context: this
    });
  }
  setFocusableElements() {
    var _this$modalContent;
    this.focusableElements = (_this$modalContent = this.modalContent) === null || _this$modalContent === void 0 ? void 0 : _this$modalContent.querySelectorAll('a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]');
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
  }
  enableFocusWithinModal() {
    var _this$firstFocusableE;
    this.setFocusableElements();
    this.focusableElements.forEach(element => {
      if (element.getAttribute('tabindex') === '-1') {
        element.setAttribute('tabindex', '0');
      }
    });

    // Set focus to the first focusable element
    (_this$firstFocusableE = this.firstFocusableElement) === null || _this$firstFocusableE === void 0 || _this$firstFocusableE.focus();
  }
  disableFocusWithinModal() {
    var _this$triggerElement;
    this.focusableElements.forEach(element => {
      if (element.getAttribute('tabindex') === '0') {
        element.setAttribute('tabindex', '-1');
      }
    });

    // Return focus to the trigger element if it exists
    (_this$triggerElement = this.triggerElement) === null || _this$triggerElement === void 0 || _this$triggerElement.focus();
  }
}
customElements.define('iam-modal', iamModal);

// Helper function to create a temporary modal
export function createModal(bodyHTML) {
  var id = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : crypto.randomUUID();
  var modalElement = document.createElement('iam-modal');
  modalElement.id = id;
  modalElement.innerHTML = "\n        <div class=\"modal__overlay\"></div>\n        <div class=\"modal__popup\">\n            <div class=\"modal__content\">".concat(bodyHTML, "</div>\n            <button class=\"modal__close-button\">").concat(translate('Close', 'accessibility.close'), "</button>\n        </div>\n    ");
  document.body.appendChild(modalElement);
  return modalElement;
}